import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Search, Tag as TagIcon, Users as UsersIcon, Copy, Check, Upload, Download, AlertCircle, FileText } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

const EMPTY = { name: "", nickname: "", account_number: "", ifsc: "", bank_name: "", upi_handle: "", tags: [], notes: "" };

export default function PanelBeneficiaries() {
    const [items, setItems] = useState([]);
    const [tags, setTags] = useState([]);
    const [q, setQ] = useState("");
    const [tagFilter, setTagFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [editing, setEditing] = useState(null);  // null | 'new' | {...doc}
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [copiedId, setCopiedId] = useState("");
    const [showImport, setShowImport] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [importBusy, setImportBusy] = useState(false);

    const refresh = async () => {
        setLoading(true); setErr("");
        try {
            const params = new URLSearchParams();
            if (q.trim()) params.set("q", q.trim());
            if (tagFilter) params.set("tag", tagFilter);
            const r = await api.get(`/partner/beneficiaries${params.toString() ? `?${params}` : ""}`);
            setItems(r.data.items || []);
            setTags(r.data.tags || []);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setLoading(false); }
    };
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tagFilter]);

    const openNew = () => { setEditing("new"); setForm(EMPTY); };
    const openEdit = (b) => { setEditing(b); setForm({ ...EMPTY, ...b, tags: b.tags || [] }); };
    const close = () => { setEditing(null); setForm(EMPTY); };

    const save = async () => {
        setSaving(true); setErr("");
        try {
            if (editing === "new") {
                await api.post("/partner/beneficiaries", form);
            } else {
                await api.patch(`/partner/beneficiaries/${editing.beneficiary_id}`, form);
            }
            close(); refresh();
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setSaving(false); }
    };

    const del = async (b) => {
        if (!window.confirm(`Delete beneficiary "${b.name}"?`)) return;
        try { await api.delete(`/partner/beneficiaries/${b.beneficiary_id}`); refresh(); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };

    const addTag = (t) => {
        const trimmed = t.trim();
        if (!trimmed || form.tags.includes(trimmed)) return;
        setForm({ ...form, tags: [...form.tags, trimmed] });
    };

    const copy = async (text, id) => {
        try { await navigator.clipboard.writeText(text); setCopiedId(id); setTimeout(() => setCopiedId(""), 1500); } catch {}
    };

    const downloadTemplate = async () => {
        try {
            const r = await api.get("/partner/beneficiaries/import/template", { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = "beneficiaries-template.csv"; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };

    const runImport = async (dryRun) => {
        if (!importFile) return;
        setImportBusy(true); setErr("");
        try {
            const fd = new FormData();
            fd.append("file", importFile);
            const r = await api.post(`/partner/beneficiaries/import?dry_run=${dryRun}`, fd,
                                        { headers: { "Content-Type": "multipart/form-data" }});
            setImportResult(r.data);
            if (!dryRun) {
                // Finished: refresh list so the newly imported rows show up
                refresh();
            }
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setImportBusy(false); }
    };

    const closeImport = () => {
        setShowImport(false); setImportFile(null); setImportResult(null);
    };

    return (
        <div data-testid="panel-beneficiaries" className="p-6 space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="text-xl font-bold flex items-center gap-2"><UsersIcon className="w-5 h-5" /> Beneficiary Address Book</h1>
                    <p className="text-xs text-slate-500 mt-1">Save frequent recipients once — pick them from the dropdown in Send Payout.</p>
                </div>
                <button data-testid="bnf-new" onClick={openNew}
                        className="btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2">
                    <Plus className="w-4 h-4" /> New beneficiary
                </button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <button data-testid="bnf-import-open" onClick={() => setShowImport(true)}
                        className="px-3 py-1.5 rounded-md text-xs border border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 flex items-center gap-1.5">
                    <Upload className="w-3.5 h-3.5" /> Import CSV
                </button>
                <button data-testid="bnf-template-dl" onClick={downloadTemplate}
                        className="px-3 py-1.5 rounded-md text-xs border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-1.5">
                    <Download className="w-3.5 h-3.5" /> Download template
                </button>
            </div>

            {err && <div data-testid="bnf-err" className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">{err}</div>}

            {/* Filters */}
            <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="relative flex-1">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input data-testid="bnf-search" value={q} onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && refresh()}
                            placeholder="Search name, nickname, account, UPI, bank…"
                            className="input w-full text-sm pl-8" />
                </div>
                <div className="flex items-center gap-2 overflow-x-auto">
                    <button onClick={() => setTagFilter("")}
                            className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${tagFilter === "" ? "bg-indigo-100 text-indigo-800" : "bg-slate-100 text-slate-600"}`}>All</button>
                    {tags.map((t) => (
                        <button key={t} data-testid={`bnf-tag-${t}`} onClick={() => setTagFilter(t)}
                                className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap flex items-center gap-1 ${tagFilter === t ? "bg-indigo-100 text-indigo-800" : "bg-slate-100 text-slate-600"}`}>
                            <TagIcon className="w-3 h-3" /> {t}
                        </button>
                    ))}
                </div>
            </div>

            {/* List */}
            <div className="bg-white border border-slate-200 rounded-xl">
                {loading ? (
                    <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
                ) : items.length === 0 ? (
                    <div data-testid="bnf-empty" className="p-10 text-center text-slate-500 text-sm">
                        No beneficiaries saved yet. Click <strong>New beneficiary</strong> to add your first one.
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {items.map((b) => (
                            <li key={b.beneficiary_id} data-testid={`bnf-row-${b.beneficiary_id}`} className="p-4 flex items-start justify-between gap-3 hover:bg-slate-50">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-semibold text-sm">{b.name}</span>
                                        {b.nickname && <span className="text-xs text-slate-500">({b.nickname})</span>}
                                        {(b.tags || []).map((t) => (
                                            <span key={t} className="px-2 py-0.5 rounded-full text-[10px] bg-indigo-50 text-indigo-700 font-medium">{t}</span>
                                        ))}
                                    </div>
                                    <div className="text-xs text-slate-600 mt-1 font-mono truncate">
                                        {b.account_number && (
                                            <span className="mr-3">
                                                A/C {b.account_number} · {b.ifsc}
                                                <button onClick={() => copy(`${b.account_number}/${b.ifsc}`, b.beneficiary_id)} className="ml-1 inline-flex align-middle text-slate-400 hover:text-slate-600">
                                                    {copiedId === b.beneficiary_id ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                                                </button>
                                            </span>
                                        )}
                                        {b.upi_handle && <span className="mr-3">UPI: {b.upi_handle}</span>}
                                        {b.bank_name && <span className="text-slate-400">{b.bank_name}</span>}
                                    </div>
                                    {b.notes && <div className="text-xs text-slate-500 mt-1 italic">{b.notes}</div>}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button data-testid={`bnf-edit-${b.beneficiary_id}`} onClick={() => openEdit(b)}
                                            className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded"><Pencil className="w-3.5 h-3.5" /></button>
                                    <button data-testid={`bnf-del-${b.beneficiary_id}`} onClick={() => del(b)}
                                            className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Modal */}
            {editing && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
                    <div data-testid="bnf-modal" className="bg-white rounded-xl w-full max-w-lg p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="font-bold text-lg">{editing === "new" ? "New beneficiary" : "Edit beneficiary"}</h3>
                            <button onClick={close} className="text-slate-400 hover:text-slate-600">✕</button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Field label="Full name *" testid="bnf-f-name">
                                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input w-full text-sm" placeholder="John Doe" />
                            </Field>
                            <Field label="Nickname" testid="bnf-f-nick">
                                <input value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} className="input w-full text-sm" placeholder="Jay" />
                            </Field>
                            <Field label="Account number" testid="bnf-f-acct">
                                <input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} className="input w-full text-sm font-mono" />
                            </Field>
                            <Field label="IFSC" testid="bnf-f-ifsc">
                                <input value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} className="input w-full text-sm font-mono uppercase" />
                            </Field>
                            <Field label="Bank name" testid="bnf-f-bank">
                                <input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="input w-full text-sm" placeholder="HDFC Bank" />
                            </Field>
                            <Field label="UPI handle" testid="bnf-f-upi">
                                <input value={form.upi_handle} onChange={(e) => setForm({ ...form, upi_handle: e.target.value.toLowerCase() })} className="input w-full text-sm font-mono" placeholder="name@okaxis" />
                            </Field>
                        </div>
                        <Field label="Tags (categorise)" testid="bnf-f-tags">
                            <div className="flex flex-wrap items-center gap-1.5 border border-slate-200 rounded-md p-2 min-h-[40px]">
                                {form.tags.map((t) => (
                                    <span key={t} className="px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-800 flex items-center gap-1">
                                        {t}
                                        <button onClick={() => setForm({ ...form, tags: form.tags.filter((x) => x !== t) })} className="hover:text-rose-600">×</button>
                                    </span>
                                ))}
                                <input placeholder="Add tag + Enter"
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(e.target.value); e.target.value = ""; }}}
                                        className="flex-1 text-xs outline-none min-w-[120px]" />
                            </div>
                            <div className="text-[10px] text-slate-500 mt-1">
                                Suggestions:
                                {["Vendor", "Salary", "Personal", "Freelancer", "Refund"].map((t) => (
                                    <button key={t} onClick={() => addTag(t)} className="ml-1.5 text-indigo-600 hover:underline">+{t}</button>
                                ))}
                            </div>
                        </Field>
                        <Field label="Notes" testid="bnf-f-notes">
                            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input w-full text-sm" rows={2} placeholder="e.g. Pay on 1st of every month" />
                        </Field>
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={close} className="px-4 py-2 text-sm rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
                            <button data-testid="bnf-save" onClick={save} disabled={saving || !form.name}
                                    className="btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2">
                                {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showImport && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
                    <div data-testid="bnf-import-modal" className="bg-white rounded-xl w-full max-w-2xl p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="font-bold text-lg">Import beneficiaries from CSV</h3>
                                <p className="text-xs text-slate-500 mt-0.5">Upload a CSV, preview the valid rows, then commit.</p>
                            </div>
                            <button onClick={closeImport} className="text-slate-400 hover:text-slate-600">✕</button>
                        </div>

                        {!importResult && (
                            <>
                                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 flex items-start gap-2">
                                    <FileText className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                                    <div>
                                        Columns (header row): <code className="font-mono text-[11px]">name, nickname, account_number, ifsc, bank_name, upi_handle, tags, notes</code>.
                                        <br />Separate tags with <code>;</code>. Either <strong>account_number + ifsc</strong> OR <strong>upi_handle</strong> is required per row.
                                        <button onClick={downloadTemplate} className="ml-1.5 text-indigo-600 hover:underline">Download the template →</button>
                                    </div>
                                </div>
                                <input type="file" accept=".csv,text/csv" data-testid="bnf-import-file"
                                        onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                                        className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100" />
                                <div className="flex justify-end gap-2 pt-2">
                                    <button onClick={closeImport} className="px-4 py-2 text-sm rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
                                    <button data-testid="bnf-import-preview" onClick={() => runImport(true)} disabled={!importFile || importBusy}
                                            className="btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                                        {importBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                        Preview (dry run)
                                    </button>
                                </div>
                            </>
                        )}

                        {importResult && (
                            <div className="space-y-3" data-testid="bnf-import-result">
                                <div className="grid grid-cols-3 gap-2">
                                    <StatBox label="Rows found" value={importResult.total} tone="slate" />
                                    <StatBox label="Valid" value={importResult.valid} tone="emerald" />
                                    <StatBox label={importResult.dry_run ? "Would skip" : "Skipped"} value={importResult.skipped} tone={importResult.skipped > 0 ? "amber" : "slate"} />
                                </div>

                                {!importResult.dry_run && (
                                    <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-xs text-emerald-900 flex items-center gap-2">
                                        <Check className="w-4 h-4" />
                                        <strong>{importResult.imported}</strong> beneficiar{importResult.imported === 1 ? "y" : "ies"} imported.
                                    </div>
                                )}

                                {importResult.errors.length > 0 && (
                                    <div className="border border-amber-200 bg-amber-50 rounded-md p-3">
                                        <div className="text-xs font-semibold text-amber-900 flex items-center gap-1.5 mb-2">
                                            <AlertCircle className="w-3.5 h-3.5" /> {importResult.errors.length} row(s) skipped
                                        </div>
                                        <ul className="text-[11px] text-amber-800 space-y-1 max-h-40 overflow-y-auto">
                                            {importResult.errors.map((e, i) => (
                                                <li key={i} data-testid={`bnf-import-err-${i}`}>
                                                    <span className="font-mono">row {e.row_number || "—"}</span>: <strong>{e.name || "(no name)"}</strong> — {e.error}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {importResult.dry_run && importResult.preview?.length > 0 && (
                                    <div className="border border-slate-200 rounded-md">
                                        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold">Preview — first {importResult.preview.length} valid rows</div>
                                        <ul className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
                                            {importResult.preview.map((r, i) => (
                                                <li key={i} className="p-2 text-xs flex items-center justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <div className="font-medium truncate">{r.name}</div>
                                                        <div className="text-[10px] text-slate-500 font-mono truncate">
                                                            {r.account_number ? `${r.account_number} · ${r.ifsc}` : r.upi_handle}
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-1 flex-wrap justify-end">
                                                        {(r.tags || []).map((t) => <span key={t} className="px-1.5 py-0.5 rounded-full text-[9px] bg-indigo-100 text-indigo-800">{t}</span>)}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="flex justify-end gap-2 pt-2">
                                    {importResult.dry_run && importResult.valid > 0 ? (
                                        <>
                                            <button onClick={() => { setImportResult(null); }} className="px-4 py-2 rounded-md border border-slate-200 text-sm hover:bg-slate-50">Choose different file</button>
                                            <button data-testid="bnf-import-commit" onClick={() => runImport(false)} disabled={importBusy}
                                                    className="btn-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-2">
                                                {importBusy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                                Import {importResult.valid} valid row{importResult.valid === 1 ? "" : "s"}
                                            </button>
                                        </>
                                    ) : (
                                        <button data-testid="bnf-import-close" onClick={closeImport} className="btn-primary px-4 py-2 rounded-md text-sm font-semibold">Done</button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function StatBox({ label, value, tone }) {
    const tones = { slate: "bg-slate-50 border-slate-200 text-slate-800",
                     emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
                     amber: "bg-amber-50 border-amber-200 text-amber-800" };
    return (
        <div className={`${tones[tone]} border rounded-md p-2 text-center`}>
            <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
            <div className="text-xl font-bold font-mono">{value}</div>
        </div>
    );
}

function Field({ label, children, testid }) {
    return (
        <div data-testid={testid}>
            <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">{label}</label>
            <div className="mt-1">{children}</div>
        </div>
    );
}
